import type { Component, ComponentMetaData, PageData, PopupAlignment } from '@wix/thunderbolt-becky-types'
import type {
	CompData,
	OverridesData,
	AutoHeightComponents } from '@wix/thunderbolt-becky-root'
import {
	getMeshResult,
	getMeshResultsForMasterPage,
	getMeshResultForHoverBox,
	sortMasterPageChildrenIntoGroups
} from '@wix/thunderbolt-becky-root'
import type { MeshChild, MeshCompNodeDeps } from './mesh.types'
import { assign, find, get, includes, isEmpty, keyBy, map, mapValues, pick } from 'lodash'
import { DEFAULT_DESKTOP_SITE_WIDTH, MOBILE_SITE_WIDTH } from '../../utils/constants'
// When merging use the types in mesh.types.ts
import { CONTAINER_TYPES, GHOST_COMP_TYPE, meshIgnoredComponentTypes } from '@wix/thunderbolt-commons'

export const shouldCalculateMesh = (component: Component, hasResponsiveLayout: boolean) =>
	component.componentType in CONTAINER_TYPES &&
	!meshIgnoredComponentTypes[component.componentType] &&
	!isEmpty(component.layout) &&
	!hasResponsiveLayout

export const getHeaderBottom = (headerLayout: Component['layout']) => {
	if (!headerLayout) {
		return 0
	}
	const headerTop = headerLayout.fixedPosition! ? 0 : headerLayout.y!
	return headerLayout.height! + headerTop
}

export const getMeshOverridesData = (deps: MeshCompNodeDeps): OverridesData => {
	return {
		siteWidth: (deps.meshComp!.isMobileView && MOBILE_SITE_WIDTH) || deps.siteWidth || DEFAULT_DESKTOP_SITE_WIDTH,
		isMobileView: deps.meshComp!.isMobileView,
		soapTopOffset: deps.siteHeader?.component.layout ? getHeaderBottom(deps.siteHeader.component.layout) : 0,
	}
}

export const getCompMeshData = (component: Component, childrenStructure: Array<Component>, compData: CompData) => ({
	compStructure: component,
	compAndChildrenStructure: keyBy(childrenStructure.concat(component), (comp) => {
		return comp.id
	}),
	compData,
	isMeshCatharsisExperimentOpen: true,
})

// Will get the autoHeightWrapper boolean from the meshComponent and it's children and
// it's grandchildren if exists (byRef component case)
export const getAutoHeightComps = (compId: string, meshComp: MeshChild) => {
	const autoHeightComps: AutoHeightComponents = {}
	let currentDepth = 1 // we need data only till grand children, so we will stop on 3d level

	const processChildren = (children: Array<MeshChild>) => {
		if (++currentDepth > 3) {
			return
		}

		for (const child of children) {
			autoHeightComps[child.id] = child.autoHeightWrapper || false

			if (child.meshChildren && child.meshChildren.length > 0) {
				processChildren(child.meshChildren as Array<MeshChild>)
			}
		}
	}

	processChildren(meshComp.meshChildren)

	autoHeightComps[compId] = meshComp.autoHeightWrapper || false

	return autoHeightComps
}

// We remove hidden and ghost children from the mesh because we don't need to calculate them together
export const shouldRemoveChildFromStructure = (component: Component) =>
	(component.modes?.isHiddenByModes && isEmpty(component.modes.overrides)) ||
	component.componentType === GHOST_COMP_TYPE ||
	component.isGhost

export const getMeshForContainer = (component: Component, deps: MeshCompNodeDeps, isEditor: boolean) => {
	const childrenStructure = map(deps.meshComp!.meshChildren, 'component')
	const compMeshData = getCompMeshData(component, childrenStructure, deps.compData)
	const overridesData = getMeshOverridesData(deps)
	return getMeshResult(
		compMeshData,
		overridesData,
		deps.absoluteComps,
		isEditor ? getAutoHeightComps(component.id, deps.meshComp!) : {}
	)
}

export const getMeshForColumns = (component: Component, deps: MeshCompNodeDeps, isEditor: boolean) => {
	const columns = deps.meshComp!.meshChildren.filter((child) => child.component.componentType === 'Column') // Clear all non-column children because of #TB-8667
	const columnStructures = map(columns, 'component')
	const dataForColumn = {
		parentCompProps: deps.compData.props,
		siblings: columnStructures,
	}
	const overridesData = getMeshOverridesData(deps)

	const meshResults = columns.map((column: MeshChild) => {
		const columnChildrenStructure = map(column.meshChildren, 'component')
		const columnMeshData = assign(getCompMeshData(column.component!, columnChildrenStructure, column.compData!), {
			dataForColumn,
		})

		return getMeshResult(
			columnMeshData,
			overridesData,
			deps.absoluteComps,
			isEditor ? getAutoHeightComps(component.id, column) : {}
		)
	})

	return { meshResults }
}

export const getDataForMasterPage = (
	childrenIds: Array<string>,
	childrenStructure: Array<Component>,
	isMobileView: boolean
) => {
	const childrenStructureMap = keyBy(childrenStructure, (comp) => comp.id)
	const childrenLayout = mapValues(childrenStructureMap, 'layout')
	const masterPageChildrenGroups = sortMasterPageChildrenIntoGroups(childrenIds, childrenLayout, isMobileView)

	const childrenGroups = []
	if (masterPageChildrenGroups.soap.beforeMain.length > 0) {
		childrenGroups.push({
			type: 'SOAP' as const,
			id: 'soapBeforePagesContainer',
			components: masterPageChildrenGroups.soap.beforeMain.map((compId: string) => childrenStructureMap[compId]),
		})
	}
	if (masterPageChildrenGroups.soap.afterMain.length > 0) {
		childrenGroups.push({
			type: 'SOAP' as const,
			id: 'soapAfterPagesContainer',
			components: masterPageChildrenGroups.soap.afterMain.map((compId: string) => childrenStructureMap[compId]),
		})
	}

	return { childrenGroups }
}

export const getMeshForMasterPage = (component: Component, deps: MeshCompNodeDeps) => {
	const childrenStructure = map(deps.meshComp!.meshChildren, 'component')
	const meshData = getCompMeshData(component, childrenStructure, deps.compData)

	const compMeshData = assign(meshData, {
		dataForMasterPage: getDataForMasterPage(component.components!, childrenStructure, deps.meshComp!.isMobileView),
	})
	const overridesData = getMeshOverridesData(deps)

	const meshResult = getMeshResultsForMasterPage(
		compMeshData,
		overridesData,
		[],
		getAutoHeightComps(component.id, deps.meshComp!)
	)

	return meshResult
}

export const getHoverBoxModes = (hoverBox: Component) => {
	const definitions = get(hoverBox, ['modes', 'definitions'], [])
	return mapValues(keyBy(definitions, 'modeId'), (mode) => ({
		modeId: mode.modeId,
		hoverBoxIdSuffix: mode.type === 'HOVER' ? ':hover' : '',
	}))
}

export const getHoverBoxResolvedByMode = (hoverBox: Component, childrenStructure: Array<Component>) => {
	const modes = getHoverBoxModes(hoverBox)

	return mapValues(modes, (mode) => {
		const resolvedChildren = map(childrenStructure, (component) => {
			const componentHiddenByMode = component.modes?.isHiddenByModes
			// 1. if component is hidden by default (isHiddenByModes = true), find the override that toggles it to be visible and use its layout
			// 2. if component is visible by default, find the override for the current mode that is still visible and use its layout
			// 3. if not found and we have more than one mode overrides, use the visible layout even if not in the same mode
			const layoutOverrides = pick(
				find(component.modes?.overrides, (override) => {
					if (componentHiddenByMode) {
						return override.isHiddenByModes === false
					}
					return !override.isHiddenByModes && includes(override.modeIds, mode.modeId)
				}) ||
					find(component.modes?.overrides, (override) => {
						if (componentHiddenByMode) {
							return override.isHiddenByModes === false
						}
						return component.modes?.overrides?.length !== 1 && !override.isHiddenByModes // fallback to the visible layout even if not in the same mode
					}),
				'layout'
			)

			return {
				...component,
				...layoutOverrides,
			}
		})

		// { [modeId: string]: { [compId: string]: MeshComponent } }
		return assign(
			keyBy(resolvedChildren, (comp) => comp.id),
			{ [`${hoverBox.id}${mode.hoverBoxIdSuffix}`]: hoverBox }
		)
	})
}

export const getMeshForHoverBox = (hoverBox: Component, deps: MeshCompNodeDeps) => {
	const childrenStructure = map(deps.meshComp!.meshChildren, 'component') as Array<Component>
	const resolvedModes = getHoverBoxResolvedByMode(hoverBox, childrenStructure)
	const compMeshData = assign(getCompMeshData(hoverBox, childrenStructure, deps.compData), { resolvedModes })
	const overridesData = getMeshOverridesData(deps)
	const meshResultForDefaultMode = getMeshResultForHoverBox(hoverBox.id, compMeshData, overridesData, [], {})
	const meshResultForHoverMode = getMeshResultForHoverBox(`${hoverBox.id}:hover`, compMeshData, overridesData, [], {})

	return {
		meshResults: [meshResultForDefaultMode, meshResultForHoverMode],
	}
}

export const createMeshContainer = (component: Component, deps: MeshCompNodeDeps): Component => {
	const compId = component.id
	const newContainerId = `Container${compId}`
	const children = deps.meshComp!.meshChildren
	const metaData = {
		...component.metaData,
	}

	if (component.componentType === 'PopupContainer') {
		// Handle popup props
		const props = deps.compData?.props as PopupAlignment | undefined
		metaData.popupContainerMeshOverrides = {
			alignmentType: props?.alignmentType,
		} as ComponentMetaData['popupContainerMeshOverrides']
	} else {
		// Handle page props
		const props = deps.compData?.props as PageData['pageBackgrounds'] | undefined
		metaData.pageMeshOverrides = {
			mobile: {
				minHeight: props?.mobile?.minHeight,
			},
			desktop: {
				minHeight: props?.desktop?.minHeight,
			},
		} as ComponentMetaData['pageMeshOverrides']
	}

	// Set children parent to the new container
	children.forEach((child: MeshChild) => {
		child.component.parent = newContainerId
	})

	// Creating the new container
	const newContainer = {
		id: newContainerId,
		components: component.components,
		componentType: 'Group',
		skin: component.skin,
		type: 'Container',
		parent: compId,
		styleId: '',
		metaData,
		layout: { height: component.layout.height },
	} as Component

	// Reseting comp data
	deps.compData.data = undefined
	deps.compData.props = {} as any
	deps.compData.style = {} as any

	return newContainer as Component
}
